import { grantKindIsFlag, grantShapeValid } from '@developerz/domain';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  expiryToIso,
  GRANT_AMOUNT_MAX,
  GRANT_REASON_MAX_CHARS,
  GRANT_SUBJECT_KINDS,
  type GrantKind,
  type GrantSubject,
  type IssueGrantInput,
} from '../lib/adminGrants';
import { Field } from './Field';
import { Select } from './Select';
import { Button } from './ui/Button';

// Issue a grant against the picked subject — the only place on the platform that
// hands out free capacity, and the audited replacement for a psql session.
//
// THE KIND VOCABULARY IS THE SUBJECT'S. `GRANT_SUBJECT_KINDS` is the domain's own
// closed matrix, imported rather than restated: a `user` admits only the kinds
// that follow a person (`unlimited_fleet`, `concurrency`, `ai_devs`), because
// `boxes`/`ci_slots` are ACCOUNT ceilings and a person-scoped row of either kind
// would store, list and lift nothing. The API refuses one (`kind_not_admitted`);
// offering it would be a control whose only outcome is a 422.
//
// EACH KIND SAYS WHAT IT BUYS, in one line under the picker (`KIND_DESC`). The
// option itself stays the enum id — the word the API takes and the audit row
// records — and the table is `Record<GrantKind, …>`, so the next kind does not
// compile until an operator can read what it does. `ai_devs` (#3630) is why the
// line exists: "N free developers" used to be two rows of folklore
// (`boxes: 1` to induce a fleet, `concurrency: N-1` to scale it) that no control
// described, and the kind that replaces them is only discoverable if it is
// explained where it is picked.
//
// THE AMOUNT FIELD FOLLOWS `grantShapeValid`, the same predicate the port and the
// `grants_amount_ck` constraint gate on — through `grantKindIsFlag`, the domain's
// own predicate, rather than a comparison spelled out here. A FLAG kind carries
// no "how much", so the box is not rendered at all rather than rendered and
// ignored.
//
// EVERYTHING ELSE IS THE SERVER'S. The reason bound, the future-expiry rule, the
// subject resolve and the issuer requirement all live in the port, and its
// sentence is what the panel renders — only the two refusals a form can answer
// without a round trip (a blank reason, an unreadable expiry) are checked here.
//
// THE REASON GROWS with what is typed (`.textarea--grow`, `ui/_forms.scss`): a
// six-month-later sentence is often several lines, and the bare `.textarea`
// scrolls inside its floor. The modifier rides `classList` so `class` stays the
// bare primitive name #5730's Live test greps for.
//
// THE WHOLE FORM IS DISABLED WHILE A WRITE IS IN FLIGHT, not just the button. A
// landed grant CLEARS the draft, so an operator who starts typing the next
// reason mid-flight would watch it vanish when the first one answers — and the
// grant they then issue carries whatever survived the wipe.

/**
 * One line per kind — what it lifts and for whom. Literal `t()` calls per entry
 * (never `t(\`admin.grants_kind_${k}\`)`), so `i18n.coverage.test.ts` can see every
 * key it resolves.
 */
const KIND_DESC: Readonly<Record<GrantKind, () => string>> = {
  ai_devs: () => t('admin.grants_kind_ai_devs'),
  unlimited_fleet: () => t('admin.grants_kind_unlimited_fleet'),
  unlimited_spend: () => t('admin.grants_kind_unlimited_spend'),
  concurrency: () => t('admin.grants_kind_concurrency'),
  boxes: () => t('admin.grants_kind_boxes'),
  ci_slots: () => t('admin.grants_kind_ci_slots'),
};

/** The per-kind line is a second hint under the Kind control, one the Field does
 * not render, so the control names it beside the Field's own — first, as it
 * paints above that one. Not `grant-kind-desc`: that is the Field's derived id. */
const KIND_WHAT_ID = 'grant-kind-what';

export function AdminGrantIssueForm(props: {
  readonly subject: GrantSubject;
  readonly busy: boolean;
  readonly onIssue: (input: IssueGrantInput) => Promise<boolean>;
}): JSX.Element {
  const [kind, setKind] = createSignal<GrantKind>('unlimited_fleet');
  const [amount, setAmount] = createSignal('');
  const [reason, setReason] = createSignal('');
  const [expires, setExpires] = createSignal('');
  const [error, setError] = createSignal<string | null>(null);

  const admitted = (): readonly GrantKind[] => GRANT_SUBJECT_KINDS[props.subject.kind];
  /** The picked kind, or the first admitted one when the subject axis changed under it. */
  const effectiveKind = (): GrantKind =>
    admitted().includes(kind()) ? kind() : (admitted()[0] as GrantKind);
  // `grantKindIsFlag`, never a comparison written out here. This line WAS
  // `!== 'unlimited_fleet'` — the same inline restatement that had already been
  // found and centralised at two other write edges — and it failed the same way:
  // it missed `unlimited_spend`, so picking that kind rendered a REQUIRED amount
  // box for a flag, and the form then refused its own submission through
  // `grantShapeValid` on the next line. The predicate is the one the port and
  // `grants_amount_ck` gate on, so a sixth kind decides its side once, in the
  // domain.
  const needsAmount = (): boolean => !grantKindIsFlag(effectiveKind());

  const submit = async (): Promise<void> => {
    if (props.busy) return;
    const why = reason().trim();
    // The one field this table exists for: a grant nobody explained is the
    // unauditable state it replaced, so an empty box never becomes a request.
    if (why === '') {
      setError(t('admin.grants_reason_required'));
      return;
    }
    // `Number`, never `parseInt`: the predicate below is what refuses a fraction,
    // and `parseInt` truncated one before it could (`1.5` issued 1, `2e3` issued 2).
    const typed = amount().trim();
    const parsed = needsAmount() && typed !== '' ? Number(typed) : Number.NaN;
    const value = Number.isNaN(parsed) ? null : parsed;
    if (!grantShapeValid({ kind: effectiveKind(), amount: needsAmount() ? value : null })) {
      // The bound is NAMED, not implied: `grantShapeValid` refuses `0`, a
      // fraction and anything above `GRANT_AMOUNT_MAX`, and a message that says
      // only "a positive whole number" leaves an operator who typed `2000`
      // staring at a field they believe is valid.
      setError(t('admin.grants_amount_required', { max: GRANT_AMOUNT_MAX }));
      return;
    }
    const expiresAt = expiryToIso(expires());
    if (expires().trim() !== '' && expiresAt === null) {
      setError(t('admin.grants_expiry_invalid'));
      return;
    }
    setError(null);
    const landed = await props.onIssue({
      subject_kind: props.subject.kind,
      subject_id: props.subject.id,
      kind: effectiveKind(),
      ...(value !== null && needsAmount() ? { amount: value } : {}),
      reason: why,
      ...(expiresAt !== null ? { expires_at: expiresAt } : {}),
    });
    // A rejected draft is KEPT — the refusal names what to fix, and clearing the
    // reason an operator just wrote would make them type it twice.
    if (!landed) return;
    setAmount('');
    setReason('');
    setExpires('');
  };

  return (
    <div class="field-group">
      <Field label={t('admin.grants_kind')} id="grant-kind" desc={t('admin.grants_kind_desc')}>
        {(a11y) => (
          <>
            <Select
              a11y={{
                id: a11y.id,
                'aria-describedby': [KIND_WHAT_ID, a11y['aria-describedby']]
                  .filter(Boolean)
                  .join(' '),
                'aria-invalid': a11y['aria-invalid'],
              }}
              label={t('admin.grants_kind')}
              block
              disabled={props.busy}
              value={effectiveKind()}
              options={admitted().map((k) => ({ value: k, label: k }))}
              onChange={(k) => setKind(k)}
            />
            <p id={KIND_WHAT_ID} class="field__desc" data-testid="grant-kind-desc">
              {KIND_DESC[effectiveKind()]()}
            </p>
          </>
        )}
      </Field>

      {/* A flag kind carries no amount at all — `grantKindIsFlag` decides which.
          `max` is the domain's own `GRANT_AMOUNT_MAX`, never a literal: `submit`
          refuses anything above it through `grantShapeValid`, and a box that
          accepts a number its own submit then rejects teaches the operator
          nothing — it read as a server fault. The copy beside it names the range
          for the same reason. */}
      <Show when={needsAmount()}>
        <Field
          label={t('admin.grants_amount')}
          id="grant-amount"
          desc={t('admin.grants_amount_desc', { max: GRANT_AMOUNT_MAX })}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="number"
              min="1"
              max={GRANT_AMOUNT_MAX}
              step="1"
              disabled={props.busy}
              value={amount()}
              onInput={(e) => setAmount(e.currentTarget.value)}
            />
          )}
        </Field>
      </Show>

      <Field
        label={t('admin.grants_reason')}
        id="grant-reason"
        desc={t('admin.grants_reason_desc')}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="textarea"
            classList={{ 'textarea--grow': true }}
            rows="2"
            maxlength={GRANT_REASON_MAX_CHARS}
            disabled={props.busy}
            value={reason()}
            onInput={(e) => setReason(e.currentTarget.value)}
          />
        )}
      </Field>

      <Field
        label={t('admin.grants_expires')}
        id="grant-expires"
        desc={t('admin.grants_expires_desc')}
      >
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="datetime-local"
            disabled={props.busy}
            value={expires()}
            onInput={(e) => setExpires(e.currentTarget.value)}
          />
        )}
      </Field>

      <Show when={error()}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>

      <div class="admin-grants__actions">
        <Button disabled={props.busy} onClick={() => void submit()}>
          {t(props.busy ? 'admin.grants_issuing' : 'admin.grants_issue')}
        </Button>
      </div>
    </div>
  );
}
